import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  FileText, Upload, CheckCircle, XCircle, Clock, Trash2, Eye, AlertCircle, Loader, ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = [
  { value: 'identity', label_tr: 'Kimlik Belgesi', label_en: 'Identity', icon: '\u{1FAAA}' },
  { value: 'diploma', label_tr: 'Diploma', label_en: 'Diploma', icon: '\u{1F393}' },
  { value: 'photo', label_tr: 'Fotoğraf', label_en: 'Photo', icon: '\u{1F4F7}' },
  { value: 'certificate', label_tr: 'Sertifika', label_en: 'Certificate', icon: '\u{1F4DC}' },
  { value: 'contract', label_tr: 'Sözleşme', label_en: 'Contract', icon: '\u{1F4DD}' },
  { value: 'other', label_tr: 'Diğer', label_en: 'Other', icon: '\u{1F4C4}' },
];

// Belge yükleme + liste ekranı (mevcut davranış). Kariyer Paneli yönlendirmesi
// devreye girene kadar (upload_enabled === true) bu görünüm çizilir.
// KORUMA NOTU: içerik/davranış bilerek değiştirilmedi, sadece prop'lara taşındı.
function DocumentsUploadView({
  documents,
  stats,
  loading,
  uploading,
  filter,
  setFilter,
  selectedType,
  setSelectedType,
  customTitle,
  setCustomTitle,
  uploadError,
  fileInputRef,
  handleFileSelect,
  handleDelete,
  handlePreview,
  lang,
}) {
  const t = (tr, en) => lang === 'tr' ? tr : en;

  const filteredDocs = filter === 'all' ? documents : documents.filter(d => d.status === filter);

  const StatusBadge = ({ status }) => {
    const config = {
      pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', icon: Clock, label_tr: 'Bekliyor', label_en: 'Pending' },
      approved: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', icon: CheckCircle, label_tr: 'Onaylandi', label_en: 'Approved' },
      rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: XCircle, label_tr: 'Reddedildi', label_en: 'Rejected' },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${c.bg} ${c.text} border ${c.border}`}>
        <Icon size={12} />
        {t(c.label_tr, c.label_en)}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-[var(--bg-app)] py-8" data-testid="documents-page">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="font-display text-3xl font-bold mb-8 flex items-center gap-3">
          <FileText size={28} strokeWidth={2.5} className="text-[var(--brand-primary)]" />
          {t('Belgelerim', 'My Documents')}
        </h1>

        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="card-clay p-4 text-center" data-testid="stat-total">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-xs text-gray-500">{t('Toplam', 'Total')}</div>
          </div>
          <div className="card-clay p-4 text-center bg-yellow-50" data-testid="stat-pending">
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <div className="text-xs text-yellow-600">{t('Bekleyen', 'Pending')}</div>
          </div>
          <div className="card-clay p-4 text-center bg-green-50" data-testid="stat-approved">
            <div className="text-2xl font-bold text-green-700">{stats.approved}</div>
            <div className="text-xs text-green-600">{t('Onayli', 'Approved')}</div>
          </div>
          <div className="card-clay p-4 text-center bg-red-50" data-testid="stat-rejected">
            <div className="text-2xl font-bold text-red-700">{stats.rejected}</div>
            <div className="text-xs text-red-600">{t('Reddedilen', 'Rejected')}</div>
          </div>
        </div>

        <div className="card-clay p-5 mb-6" data-testid="upload-section">
          <h2 className="font-bold mb-3 flex items-center gap-2">
            <Upload size={18} /> {t('Yeni Belge Yükle', 'Upload New Document')}
          </h2>

          <div className="grid grid-cols-2 gap-3 mb-3">
            {DOCUMENT_TYPES.map(dt => (
              <button
                key={dt.value}
                onClick={() => setSelectedType(dt.value)}
                className={`p-3 rounded-xl border-2 text-left transition ${
                  selectedType === dt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                data-testid={`doc-type-${dt.value}`}
              >
                <div className="text-lg mb-1">{dt.icon}</div>
                <div className="text-sm font-bold">{t(dt.label_tr, dt.label_en)}</div>
              </button>
            ))}
          </div>

          <input
            type="text"
            value={customTitle}
            onChange={e => setCustomTitle(e.target.value)}
            placeholder={t('Özel başlık (opsiyonel)', 'Custom title (optional)')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-3 text-sm"
            data-testid="custom-title-input"
          />

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            disabled={uploading}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-500 file:text-white file:font-bold hover:file:bg-blue-600 file:cursor-pointer"
            data-testid="file-input"
          />

          {uploading && (
            <div className="mt-3 flex items-center gap-2 text-sm text-blue-600">
              <Loader size={16} className="animate-spin" />
              {t('Yükleniyor...', 'Uploading...')}
            </div>
          )}

          {uploadError && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded-lg" data-testid="upload-error">
              <AlertCircle size={16} />
              {uploadError}
            </div>
          )}

          <div className="mt-2 text-xs text-gray-500">
            {t('Izin verilen formatlar', 'Allowed')}: PDF, JPG, PNG &middot; {t('Max boyut', 'Max size')}: 10 MB
          </div>
        </div>

        <div className="flex gap-2 mb-4" data-testid="filter-buttons">
          {[
            { value: 'all', label_tr: 'Hepsi', label_en: 'All' },
            { value: 'pending', label_tr: 'Bekleyen', label_en: 'Pending' },
            { value: 'approved', label_tr: 'Onayli', label_en: 'Approved' },
            { value: 'rejected', label_tr: 'Reddedilen', label_en: 'Rejected' },
          ].map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-bold transition ${
                filter === f.value
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              data-testid={`filter-${f.value}`}
            >
              {t(f.label_tr, f.label_en)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader size={32} className="animate-spin mx-auto text-blue-500" />
          </div>
        ) : filteredDocs.length === 0 ? (
          <div className="card-clay p-12 text-center" data-testid="empty-state">
            <FileText size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="font-bold text-gray-500">{t('Bu filtrede belge yok', 'No documents in this filter')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDocs.map(doc => {
              const typeConf = DOCUMENT_TYPES.find(d => d.value === doc.document_type);
              return (
                <motion.div
                  key={doc.document_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="card-clay p-4 flex items-center gap-4"
                  data-testid={`document-row-${doc.document_id}`}
                >
                  <div className="text-3xl flex-shrink-0">{typeConf?.icon || '\u{1F4C4}'}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{doc.title}</div>
                    <div className="text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                      <span>{doc.original_filename}</span>
                      <span>{(doc.size / 1024).toFixed(1)} KB</span>
                      <span>{new Date(doc.uploaded_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}</span>
                    </div>
                    {doc.status === 'rejected' && doc.rejection_reason && (
                      <div className="mt-2 text-xs text-red-700 bg-red-50 p-2 rounded">
                        <strong>{t('Red sebebi', 'Reason')}:</strong> {doc.rejection_reason}
                      </div>
                    )}
                  </div>
                  <StatusBadge status={doc.status} />
                  <div className="flex gap-1">
                    <button
                      onClick={() => handlePreview(doc.document_id)}
                      className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100"
                      title={t('Onizle', 'Preview')}
                      data-testid={`preview-doc-${doc.document_id}`}
                    >
                      <Eye size={14} />
                    </button>
                    {doc.status !== 'approved' && (
                      <button
                        onClick={() => handleDelete(doc.document_id)}
                        className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                        title={t('Sil', 'Delete')}
                        data-testid={`delete-doc-${doc.document_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Belge yükleme geçici olarak kapalı — kullanıcı Kariyer Paneli'ne yönlendirilir.
// Özellik SİLİNMEDİ: backend upload_enabled=true döndüğünde DocumentsUploadView
// (yukarıda, davranışı korunmuş halde) tekrar devreye girer.
function DocumentsRedirectView() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-[var(--bg-app)] p-4" data-testid="documents-redirect-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="max-w-xl mx-auto mt-16 bg-white card-clay p-6 text-center"
        role="region"
        aria-labelledby="documents-redirect-title"
      >
        <div className="w-14 h-14 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-4">
          <ExternalLink size={26} strokeWidth={2.5} className="text-[var(--brand-primary)]" />
        </div>
        <h1 id="documents-redirect-title" className="font-display font-bold text-xl mb-2">
          {t('documents.redirect.title')}
        </h1>
        <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5">
          {t('documents.redirect.body')}
        </p>
        <a
          href="https://kariyerpaneli.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-[var(--brand-primary)] border-2 border-b-4 border-[var(--brand-primary-shadow)] active:translate-y-1 active:border-b-2 transition-all duration-100 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:ring-offset-2"
          data-testid="kariyer-paneli-cta"
          aria-label={t('documents.redirect.ariaLabel')}
        >
          <ExternalLink size={16} />
          {t('documents.redirect.cta')}
        </a>
        <p className="text-xs text-[var(--text-secondary)] leading-relaxed mt-4 text-center">
          {t('documents.redirect.thirdParty')}
        </p>
        <p className="text-xs text-[var(--text-secondary)] mt-2 text-center">
          {t('documents.redirect.note')}
        </p>
      </motion.div>
    </div>
  );
}

export default function DocumentsPage() {
  const { authHeaders } = useAuth();
  const { lang } = useLanguage();
  const toast = useToast();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [selectedType, setSelectedType] = useState(DOCUMENT_TYPES[0].value);
  const [customTitle, setCustomTitle] = useState('');
  const [uploadError, setUploadError] = useState('');
  // Feature flag: backend GET /documents/me -> upload_enabled. Savunmacı varsayılan: false
  // (alan yoksa/undefined ise kapalı kabul edilir, backend henüz deploy edilmemiş olabilir).
  const [uploadEnabled, setUploadEnabled] = useState(false);
  const [flagReady, setFlagReady] = useState(false);
  const fileInputRef = useRef(null);

  const t = (tr, en) => lang === 'tr' ? tr : en;

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/documents/me`, { headers: authHeaders() });
      setDocuments(res.data.documents || []);
      setStats(res.data.stats || { total: 0, pending: 0, approved: 0, rejected: 0 });
      setUploadEnabled(res.data?.upload_enabled === true);
    } catch (e) {
      console.error('Belgeler alinamadi:', e);
      setUploadEnabled(false);
    } finally {
      setLoading(false);
      setFlagReady(true);
    }
  };

  useEffect(() => { fetchDocuments(); }, []); // eslint-disable-line

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadError('');

    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      setUploadError(t('Sadece PDF, JPG, PNG kabul edilir', 'Only PDF, JPG, PNG allowed'));
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError(t('Dosya cok buyuk (max 10 MB)', 'File too large (max 10 MB)'));
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const params = new URLSearchParams({
        document_type: selectedType,
        title: customTitle.trim(),
      });

      await axios.post(
        `${API}/documents/upload?${params.toString()}`,
        formData,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
      );

      setCustomTitle('');
      await fetchDocuments();
    } catch (err) {
      setUploadError(err.response?.data?.detail || t('Yükleme başarısız', 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(t('Bu belgeyi silmek istediğinize emin misiniz?', 'Delete this document?'))) return;
    try {
      await axios.delete(`${API}/documents/${docId}`, { headers: authHeaders() });
      toast.success(t('Belge silindi', 'Document deleted'));
      await fetchDocuments();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('Silme başarısız', 'Delete failed'));
    }
  };

  const handlePreview = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/preview`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('Onizleme acilamadi', 'Preview failed'));
    }
  };

  // Flag bilinmeden karar verme: ilk fetch tamamlanana kadar bekleme ekranı.
  if (!flagReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-app)]" data-testid="documents-page-loading">
        <Loader size={32} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }

  if (!uploadEnabled) {
    return <DocumentsRedirectView />;
  }

  return (
    <DocumentsUploadView
      documents={documents}
      stats={stats}
      loading={loading}
      uploading={uploading}
      filter={filter}
      setFilter={setFilter}
      selectedType={selectedType}
      setSelectedType={setSelectedType}
      customTitle={customTitle}
      setCustomTitle={setCustomTitle}
      uploadError={uploadError}
      fileInputRef={fileInputRef}
      handleFileSelect={handleFileSelect}
      handleDelete={handleDelete}
      handlePreview={handlePreview}
      lang={lang}
    />
  );
}
